import PropTypes from 'prop-types';
import ScheduleWindow from '../ScheduleWindow';

/**
 * One building: when it is open, and whether it is shut.
 *
 * TWO SAVE BUTTONS, ON PURPOSE. Hours are a rule that runs itself; a closure is
 * a decision somebody made, and it is the one thing on this page that every
 * player sees the moment it is pressed. The server keeps them on separate
 * endpoints for the same reason — so a form left open in another tab cannot
 * reopen a shop somebody shut five minutes ago.
 *
 * TWO KINDS OF TIME LIVE IN THIS PANEL, and the difference is the likeliest bug
 * in the whole feature. The hours are WALL CLOCK in the business's own timezone,
 * so 9am stays 9am across daylight saving. The reopen field is an ABSOLUTE
 * INSTANT read from the admin's browser. Those are not the same thing, so the
 * resolved moment is echoed back underneath in words — without it there is no
 * way to tell which one you just typed.
 */

const field = 'mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-900';

const CLOSURES = [
  { id: 'none', name: 'Open as scheduled' },
  { id: 'temporary', name: 'Temporarily closed' },
  { id: 'permanent', name: 'Permanently closed' },
  { id: 'soon', name: 'Coming soon' },
];

/** `datetime-local` wants 'YYYY-MM-DDTHH:MM' in local time, not an ISO instant. */
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** What a row actually means, said back in words. */
const describe = (w) => {
  if (!w.from && !w.to) return 'all day';
  if (!w.from || !w.to) return 'all day — a window needs both times or neither';
  if (w.from === w.to) return 'all day';
  if (w.to < w.from) return 'overnight, into the following morning';
  return '';
};

function BusinessInspector({
  business, draft, onDraftChange, onSaveHours, onSetClosure, onClear, busy,
}) {
  if (!business) {
    return (
      <p className="rounded border border-dashed border-gray-300 p-3 text-xs text-gray-500 dark:border-gray-700">
        Pick a building on the left to set when it is open.
        <br />
        <br />
        A building with nothing set is always open, which is what every building
        was before this existed.
      </p>
    );
  }

  const { hours = [], closure = 'none', closedUntil = '' } = draft;

  const setWindow = (index, next) => onDraftChange({
    hours: hours.map((w, i) => (i === index ? next : w)),
  });

  return (
    <div className="space-y-4 rounded border border-gray-200 p-3 dark:border-gray-800">
      <div>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {business.label || 'Unnamed building'}
        </h2>
        <p className="text-xs text-gray-500">
          {business.kind}
          {' · '}
          {business.zoneId}
        </p>
        {!business.label && (
          // The world draws nothing over an unlabelled building, so "closed"
          // would never appear on it. Worth saying before somebody authors a
          // schedule that can only be seen on this page.
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            This building has no name, so nothing is drawn over it in the world —
            including whether it is shut. Name it in the world editor first.
          </p>
        )}
      </div>

      <fieldset className="rounded border border-gray-300 p-3 dark:border-gray-700">
        <legend className="px-1 text-sm text-gray-700 dark:text-gray-300">
          When it is open
          <span className="ml-1 text-xs text-gray-500">
            (times are
            {' '}
            {business.timeZone}
            )
          </span>
        </legend>

        {hours.length === 0 && (
          <p className="text-xs text-gray-500">
            No hours set, so it is open around the clock.
          </p>
        )}

        <div className="space-y-3">
          {hours.map((w, i) => {
            const note = describe(w);

            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="rounded border border-gray-200 p-2 dark:border-gray-800">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs text-gray-500">{`Window ${i + 1}`}</span>
                  <button
                    type="button"
                    onClick={() => onDraftChange({ hours: hours.filter((_, j) => j !== i) })}
                    className="rounded px-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                  >
                    ✕
                  </button>
                </div>

                <ScheduleWindow
                  idPrefix={`window-${i}`}
                  value={w}
                  onChange={(next) => setWindow(i, next)}
                />

                {note && <p className="mt-2 text-[11px] text-gray-500">{note}</p>}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onDraftChange({ hours: [...hours, { days: [], from: '', to: '' }] })}
          className="mt-3 rounded bg-gray-200 px-2 py-1 text-xs text-gray-800 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-200"
        >
          Add hours
        </button>

        <p className="mt-2 text-xs text-gray-500">
          Open if ANY window matches, so use one row per pattern — weekdays 9 to
          5 and Saturday 10 to 2 are two rows. A window that ends before it
          starts runs overnight. Checked every 30 seconds, so it is accurate to
          about half a minute. A row with no days and no times is dropped, since
          it would hold the place open around the clock.
        </p>

        <button
          type="button"
          onClick={onSaveHours}
          disabled={busy}
          className="mt-3 rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Save hours
        </button>
      </fieldset>

      <fieldset className="rounded border border-gray-300 p-3 dark:border-gray-700">
        <legend className="px-1 text-sm text-gray-700 dark:text-gray-300">Shut it</legend>

        <div className="space-y-1 text-sm">
          {CLOSURES.map((c) => (
            <label key={c.id} className="flex items-center gap-2" htmlFor={`closure-${c.id}`}>
              <input
                id={`closure-${c.id}`}
                type="radio"
                name="closure"
                checked={closure === c.id}
                onChange={() => onDraftChange({ closure: c.id })}
              />
              {c.name}
            </label>
          ))}
        </div>

        {closure === 'temporary' && (
          <label className="mt-3 block text-sm" htmlFor="closed-until">
            <span className="text-gray-700 dark:text-gray-300">Reopens at (optional)</span>
            <input
              id="closed-until"
              type="datetime-local"
              value={toLocalInput(closedUntil)}
              onChange={(e) => onDraftChange({ closedUntil: e.target.value })}
              className={field}
            />
            <span className="mt-1 block text-[11px] text-gray-500">
              {closedUntil
                // Echoed back deliberately: this field is read in YOUR timezone,
                // while the hours above are in the business's. Nothing else on
                // screen distinguishes them.
                ? `Reopens ${new Date(closedUntil).toLocaleString()} — your local time. It lapses on its own; nothing needs clearing.`
                : 'Leave blank to stay shut until you say otherwise.'}
            </span>
          </label>
        )}

        <button
          type="button"
          onClick={onSetClosure}
          disabled={busy}
          className="mt-3 rounded bg-amber-600 px-3 py-1 text-sm text-white hover:bg-amber-700 disabled:opacity-50"
        >
          Apply now
        </button>
      </fieldset>

      <button
        type="button"
        onClick={onClear}
        disabled={busy}
        className="text-xs text-red-600 hover:underline disabled:opacity-50"
      >
        Clear everything — back to always open
      </button>
    </div>
  );
}

BusinessInspector.propTypes = {
  business: PropTypes.shape({
    zoneId: PropTypes.string,
    label: PropTypes.string,
    kind: PropTypes.string,
    timeZone: PropTypes.string,
  }),
  draft: PropTypes.shape({
    hours: PropTypes.arrayOf(PropTypes.shape({})),
    closure: PropTypes.string,
    closedUntil: PropTypes.string,
  }).isRequired,
  onDraftChange: PropTypes.func.isRequired,
  onSaveHours: PropTypes.func.isRequired,
  onSetClosure: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

BusinessInspector.defaultProps = { business: null, busy: false };

export default BusinessInspector;
